/* ═══════════════════════════════════════════════════════════════════════
   assets/quiz-template-vitalia.js

   Vitalia® Circulation Quiz runtime.

   Routing logic per the brief's Segmentation Logic Table:
   - Each selected answer can score across MULTIPLE buckets (primary +
     extras from data-answer-extra="bucket:weight,bucket:weight").
   - Compound override: if Q3 (the dominant routing block) records 3+
     non-exclusive selections, route to Compound regardless of scores.
   - Tiebreaker: Q3's primary bucket wins ties.
   - Each answer can carry a `data-answer-overlay` that, once selected,
     gets passed to the result page as ?overlay= for hero-block swapping.
   ═══════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  // Order matters for tiebreaks when Q3 isn't decisive.
  var BUCKETS = ['generate', 'shield', 'route', 'compound'];

  function init() {
    var root = document.querySelector('[data-quiz-section]');
    if (!root) return;

    var debug = root.dataset.debug === 'true';
    var log = debug ? function () { console.log.apply(console, ['[VitaliaQuiz]'].concat([].slice.call(arguments))); } : function () {};

    var blocks = Array.prototype.slice.call(root.querySelectorAll('.quiz-block'));
    if (blocks.length === 0) { log('No blocks found.'); return; }

    var backBtn = root.querySelector('[data-quiz-back]');
    var stepLabel = root.querySelector('[data-quiz-step]');
    var progressFill = root.querySelector('[data-quiz-progress]');
    var headerEl = root.querySelector('[data-quiz-header]');

    var scoringMode = root.dataset.scoringMode || 'multi_category';
    var fallbackUrl = root.dataset.fallbackUrl || '/';
    var forwardUtm = root.dataset.forwardUtm === 'true';
    var storageKey = root.dataset.storageKey || 'quiz:vitalia';

    var bucketUrls = {
      generate: root.dataset.urlGenerate || '',
      shield:   root.dataset.urlShield   || '',
      route:    root.dataset.urlRoute    || '',
      compound: root.dataset.urlCompound || '',
    };

    var sessionId = generateSessionId();
    var startedAt = Date.now();
    var history = [0];
    var currentIdx = 0;
    // answers shape: { block_idx: [{ answer_idx, value, bucket, weight, exclusive, extra, overlay }] }
    var answers = {};
    var scores = { generate: 0, shield: 0, route: 0, compound: 0 };
    var capturedOverlays = []; // priority-ordered list of overlay keys

    log('Initialized', { blockCount: blocks.length, scoringMode: scoringMode, bucketUrls: bucketUrls });

    rehydrate();
    bindAllBlocks();
    updateChrome();

    /* ───── Block binding ───── */

    function bindAllBlocks() {
      blocks.forEach(function (block, idx) {
        var blockIdx = idx + 1;
        var blockType = block.dataset.blockType;

        var labels = Array.prototype.slice.call(block.querySelectorAll('.qt-arow, .qt-acard-img, .qt-yn-card'));
        var inputs = Array.prototype.slice.call(block.querySelectorAll('.qt-answer__input'));
        var inputType = inputs.length > 0 ? inputs[0].type : 'radio';

        labels.forEach(function (label) {
          var input = label.querySelector('.qt-answer__input');
          if (!input) return;
          input.addEventListener('change', function () {
            handleAnswerChange(block, blockIdx, label, input, inputType);
          });
        });

        var nextBtn = block.querySelector('.qt-next');
        if (nextBtn) {
          nextBtn.addEventListener('click', function () {
            advanceFrom(blockIdx);
          });
        }

        var finishBtn = block.querySelector('.qt-finish');
        if (finishBtn) {
          finishBtn.addEventListener('click', function () {
            handleFinish(block, finishBtn);
          });
        }
      });

      if (backBtn) {
        backBtn.addEventListener('click', goBack);
      }
    }

    /* ───── Answer change ───── */

    function handleAnswerChange(block, blockIdx, label, input, inputType) {
      var record = readAnswerData(label, input);

      if (inputType === 'radio') {
        var sibLabels = block.querySelectorAll('.qt-arow, .qt-acard-img, .qt-yn-card');
        sibLabels.forEach(function (l) { l.classList.remove('qt-selected'); });
        label.classList.add('qt-selected');

        answers[blockIdx] = [record];

        log('Radio selected', { blockIdx: blockIdx, value: record.value, bucket: record.bucket, extra: record.extra, overlay: record.overlay });

        var hasNextBtn = block.querySelector('.qt-next');
        if (!hasNextBtn) {
          setTimeout(function () { advanceFrom(blockIdx); }, 360);
        } else {
          hasNextBtn.disabled = false;
        }

      } else if (inputType === 'checkbox') {
        var siblingInputs = Array.prototype.slice.call(block.querySelectorAll('.qt-answer__input'));

        if (record.exclusive && input.checked) {
          // Exclusive answer just got picked → clear all others
          siblingInputs.forEach(function (i) {
            if (i !== input) {
              i.checked = false;
              var sibLabel = i.closest('label');
              if (sibLabel) sibLabel.classList.remove('qt-selected');
            }
          });
        } else if (input.checked) {
          // Non-exclusive got picked → clear any exclusive sibling
          siblingInputs.forEach(function (i) {
            var sibLabel = i.closest('label');
            if (sibLabel && sibLabel.dataset.answerExclusive === 'true') {
              i.checked = false;
              sibLabel.classList.remove('qt-selected');
            }
          });
        }

        if (input.checked) label.classList.add('qt-selected');
        else label.classList.remove('qt-selected');

        // Collect current selections
        var currentSelections = [];
        siblingInputs.forEach(function (i) {
          if (i.checked) {
            var l = i.closest('label');
            currentSelections.push(readAnswerData(l, i));
          }
        });
        answers[blockIdx] = currentSelections;

        var continueBtn = block.querySelector('.qt-next');
        if (continueBtn) {
          var minSel = parseInt(continueBtn.dataset.minSelections, 10) || 1;
          continueBtn.disabled = currentSelections.length < minSel;
        }

        log('Checkbox state', { blockIdx: blockIdx, selections: currentSelections });
      }

      persist();
    }

    function readAnswerData(label, input) {
      return {
        answer_idx: parseInt(label.dataset.answerIndex, 10) || 0,
        value: input.value,
        bucket: label.dataset.answerBucket || 'none',
        weight: parseInt(label.dataset.answerWeight, 10) || 0,
        exclusive: label.dataset.answerExclusive === 'true',
        extra: label.dataset.answerExtra || '',
        overlay: label.dataset.answerOverlay || 'none',
      };
    }

    /* ───── Navigation ───── */

    function advanceFrom(fromIdx) {
      var block = blockAt(fromIdx);
      if (!block) return;

      var branchStr = block.dataset.branchRules;
      var targetIdx = null;
      if (branchStr && answers[fromIdx] && answers[fromIdx].length === 1) {
        var rules = parseBranchRules(branchStr);
        var ansN = answers[fromIdx][0].answer_idx;
        if (rules[ansN]) targetIdx = rules[ansN];
      }
      if (!targetIdx) targetIdx = fromIdx + 1;

      if (targetIdx > blocks.length) {
        handleFinish(block, null);
        return;
      }
      showBlock(targetIdx);
    }

    function goBack() {
      if (history.length <= 1) return;
      history.pop();
      var targetIdx = history[history.length - 1];
      showBlockNoHistory(targetIdx);
    }

    function showBlock(idx) {
      history.push(idx);
      showBlockNoHistory(idx);
    }

    function showBlockNoHistory(idx) {
      blocks.forEach(function (b, i) {
        var thisIdx = i + 1;
        if (thisIdx === idx) b.classList.add('quiz-block--active');
        else b.classList.remove('quiz-block--active');
      });
      currentIdx = idx;
      window.scrollTo({ top: 0, behavior: 'smooth' });

      var block = blockAt(idx);
      if (block && block.dataset.blockType === 'loading') {
        startLoadingSequence(block);
      }

      updateChrome();
      persist();
      log('Show block', idx);
    }

    function blockAt(idx) {
      return blocks[idx - 1] || null;
    }

    function parseBranchRules(str) {
      var rules = {};
      str.split(',').forEach(function (r) {
        var p = r.trim().split('>');
        if (p.length === 2) {
          var a = parseInt(p[0].trim(), 10);
          var t = parseInt(p[1].trim(), 10);
          if (!isNaN(a) && !isNaN(t)) rules[a] = t;
        }
      });
      return rules;
    }

    /* ───── Chrome (header / progress / back) ───── */

    function updateChrome() {
      if (backBtn) {
        backBtn.style.display = (currentIdx > 1) ? '' : 'none';
      }

      var currentBlock = blockAt(currentIdx);

      if (stepLabel) {
        if (currentBlock && currentBlock.dataset.blockType === 'quiz-question') {
          var qBlocks = blocks.filter(function (b) { return b.dataset.blockType === 'quiz-question'; });
          var qIdxInList = qBlocks.indexOf(currentBlock) + 1;
          stepLabel.textContent = qIdxInList + ' of ' + qBlocks.length;
        } else {
          stepLabel.textContent = '';
        }
      }

      if (progressFill) {
        var pct = Math.round((currentIdx / blocks.length) * 100);
        progressFill.style.width = pct + '%';
      }

      if (headerEl) {
        if (currentBlock && currentBlock.dataset.blockType === 'loading') {
          headerEl.style.display = 'none';
        } else {
          headerEl.style.display = '';
        }
      }
    }

    /* ───── Loading block sequencer ───── */

    function startLoadingSequence(block) {
      var wrap = block.querySelector('.qt-loading-wrap');
      if (!wrap || wrap.dataset.loadingStarted === 'true') return;
      wrap.dataset.loadingStarted = 'true';

      var DURATION = parseInt(wrap.dataset.loadingDuration, 10) || 7200;
      var pctEl = wrap.querySelector('[data-loading-pct]');
      var barEl = wrap.querySelector('[data-loading-bar]');
      var stages = Array.prototype.slice.call(wrap.querySelectorAll('.qt-loading__stage'));
      var reassures = Array.prototype.slice.call(wrap.querySelectorAll('.qt-loading__reassurance-line'));
      var reassureIdx = 0;

      var start = Date.now();
      var raf;
      var rotId;

      function tick() {
        var t = Math.min(1, (Date.now() - start) / DURATION);
        if (pctEl) pctEl.textContent = Math.round(t * 100) + '%';
        if (barEl) barEl.style.width = (t * 100) + '%';
        var stageIdx = Math.min(stages.length - 1, Math.floor(t * stages.length));
        stages.forEach(function (s, i) {
          s.classList.toggle('qt-loading__stage--done', i < stageIdx);
          s.classList.toggle('qt-loading__stage--active', i === stageIdx);
          s.classList.toggle('qt-loading__stage--idle', i > stageIdx);
        });
        if (t < 1) raf = requestAnimationFrame(tick);
        else {
          if (rotId) clearInterval(rotId);
          setTimeout(function () { advanceFrom(currentIdx); }, 380);
        }
      }
      raf = requestAnimationFrame(tick);

      if (reassures.length > 1) {
        reassures[0].classList.add('qt-loading__reassurance-line--active');
        rotId = setInterval(function () {
          reassures[reassureIdx].classList.remove('qt-loading__reassurance-line--active');
          reassureIdx = (reassureIdx + 1) % reassures.length;
          reassures[reassureIdx].classList.add('qt-loading__reassurance-line--active');
        }, 2200);
      } else if (reassures.length === 1) {
        reassures[0].classList.add('qt-loading__reassurance-line--active');
      }
    }

    /* ───── Finish: compute, route, redirect ───── */

    function handleFinish(block, btn) {
      computeScores();
      collectOverlays();
      log('Scores', scores, 'Overlays', capturedOverlays);

      var url;
      var strategy = btn ? btn.dataset.redirectStrategy : 'highest_bucket';
      var explicitUrl = btn ? btn.dataset.redirectUrl : '';
      var bestBucket = null;

      if (strategy === 'none') return;
      if (strategy === 'explicit_url' && explicitUrl) {
        url = explicitUrl;
      } else {
        bestBucket = resolveRoute();
        if (bestBucket && bucketUrls[bestBucket]) {
          url = bucketUrls[bestBucket];
        } else {
          url = fallbackUrl;
        }
      }

      url = buildResultUrl(url, bestBucket);
      log('Redirecting to', url);

      try { sessionStorage.removeItem(storageKey); } catch (e) {}
      setTimeout(function () { window.location.href = url; }, 50);
    }

    /* ───── Scoring & routing ───── */

    function computeScores() {
      scores = { generate: 0, shield: 0, route: 0, compound: 0 };
      Object.keys(answers).forEach(function (blockIdx) {
        var sels = answers[blockIdx];
        if (!Array.isArray(sels)) return;
        sels.forEach(function (sel) {
          // Primary bucket
          if (sel.bucket && sel.bucket !== 'none' && scores.hasOwnProperty(sel.bucket)) {
            scores[sel.bucket] += (sel.weight || 0);
          }
          // Extra buckets — parse "shield:1,compound:1"
          if (sel.extra) {
            sel.extra.split(',').forEach(function (pair) {
              var parts = pair.trim().split(':');
              if (parts.length !== 2) return;
              var b = parts[0].trim().toLowerCase();
              var w = parseInt(parts[1].trim(), 10);
              if (scores.hasOwnProperty(b) && !isNaN(w)) {
                scores[b] += w;
              }
            });
          }
        });
      });
    }

    function collectOverlays() {
      capturedOverlays = [];
      Object.keys(answers).forEach(function (blockIdx) {
        var sels = answers[blockIdx];
        if (!Array.isArray(sels)) return;
        sels.forEach(function (sel) {
          if (sel.overlay && sel.overlay !== 'none' && capturedOverlays.indexOf(sel.overlay) === -1) {
            capturedOverlays.push(sel.overlay);
          }
        });
      });
    }

    /**
     * Resolve the result-page bucket per the brief.
     *
     * 1. Compound override A: if Q3 (the symptom block) recorded 3+
     *    non-exclusive selections, route to Compound.
     * 2. Compound override B: if two or more buckets score within 1
     *    point of each other AND Q3 included the "all of these"
     *    exclusive answer OR Q4 included the "longer than I want to
     *    admit" answer, route to Compound.
     * 3. Highest bucket wins.
     * 4. Tiebreaker: Q3's primary bucket wins among tied buckets.
     * 5. Otherwise: BUCKETS iteration order tiebreak.
     */
    function resolveRoute() {
      // STEP 1 — Compound override A: 3+ Q3 selections
      var q3BlockIdx = findSymptomBlockIdx();
      if (q3BlockIdx !== null) {
        var q3Sels = answers[q3BlockIdx] || [];
        var nonExclusiveCount = q3Sels.filter(function (s) { return !s.exclusive; }).length;
        if (nonExclusiveCount >= 3) {
          log('Compound override A fired: Q3 had ' + nonExclusiveCount + ' non-exclusive selections');
          return 'compound';
        }
      }

      // Compute spread for override B check
      var sortedDesc = BUCKETS.slice().sort(function (a, b) { return scores[b] - scores[a]; });
      var topScore = scores[sortedDesc[0]];
      var nearTopBuckets = BUCKETS.filter(function (b) { return scores[b] >= topScore - 1 && scores[b] > 0; });

      // STEP 2 — Compound override B: close tie + emotional confession signals
      if (nearTopBuckets.length >= 2) {
        var q3HasAllOfThese = false;
        var q4HasLongAdmit = false;
        if (q3BlockIdx !== null) {
          (answers[q3BlockIdx] || []).forEach(function (s) {
            if (s.exclusive && s.bucket === 'compound') q3HasAllOfThese = true;
          });
        }
        var q4BlockIdx = findDurationBlockIdx();
        if (q4BlockIdx !== null) {
          (answers[q4BlockIdx] || []).forEach(function (s) {
            // "Longer than I want to admit" carries compound weight ≥2
            if (s.bucket === 'compound' && s.weight >= 2) q4HasLongAdmit = true;
          });
        }
        if (q3HasAllOfThese || q4HasLongAdmit) {
          log('Compound override B fired (close tie + emotional signal)', { q3HasAllOfThese: q3HasAllOfThese, q4HasLongAdmit: q4HasLongAdmit });
          return 'compound';
        }
      }

      // STEP 3 — Highest bucket
      if (topScore <= 0) return null;
      var tied = BUCKETS.filter(function (b) { return scores[b] === topScore; });
      if (tied.length === 1) return tied[0];

      // STEP 4 — Q3 tiebreaker
      if (q3BlockIdx !== null) {
        var q3Buckets = (answers[q3BlockIdx] || []).map(function (s) { return s.bucket; });
        for (var i = 0; i < q3Buckets.length; i++) {
          if (tied.indexOf(q3Buckets[i]) !== -1) {
            log('Q3 tiebreaker: ' + q3Buckets[i] + ' wins among', tied);
            return q3Buckets[i];
          }
        }
      }

      // STEP 5 — BUCKETS iteration order
      return tied[0];
    }

    /**
     * Find Q4 (duration block) by detecting a quiz-question block with
     * radio input where answers span generate/shield/route/compound
     * AND the highest-weight answer carries compound weight ≥2.
     * Fallback: heuristic check that this isn't the symptom block.
     */
    function findDurationBlockIdx() {
      var symptomIdx = findSymptomBlockIdx();
      for (var i = 0; i < blocks.length; i++) {
        var block = blocks[i];
        if (block.dataset.blockType !== 'quiz-question') continue;
        if ((i + 1) === symptomIdx) continue;
        var labels = block.querySelectorAll('.qt-arow, .qt-acard-img, .qt-yn-card');
        if (labels.length !== 4) continue; // Q4 has exactly 4 answers
        var firstInput = block.querySelector('.qt-answer__input');
        if (!firstInput || firstInput.type !== 'radio') continue;
        // Heuristic: Q4 has a compound-weight-2 answer
        var hasCompoundHeavy = false;
        labels.forEach(function (l) {
          if (l.dataset.answerBucket === 'compound' && parseInt(l.dataset.answerWeight, 10) >= 2) {
            hasCompoundHeavy = true;
          }
        });
        if (hasCompoundHeavy) return i + 1;
      }
      return null;
    }

    /**
     * Find the index of the symptom-cluster (Q3) block by detecting
     * the multi-select question whose answers span all four buckets.
     * This is more robust than hardcoding block index 3, since blocks
     * can be reordered in the customizer.
     */
    function findSymptomBlockIdx() {
      for (var i = 0; i < blocks.length; i++) {
        var block = blocks[i];
        if (block.dataset.blockType !== 'quiz-question') continue;
        var labels = block.querySelectorAll('.qt-arow, .qt-acard-img, .qt-yn-card');
        var bucketSet = {};
        labels.forEach(function (l) {
          var b = l.dataset.answerBucket;
          if (b && b !== 'none') bucketSet[b] = true;
        });
        var bucketsHit = Object.keys(bucketSet).length;
        // Symptom block hits 3+ buckets and is multi-select
        var firstInput = block.querySelector('.qt-answer__input');
        var isMulti = firstInput && firstInput.type === 'checkbox';
        if (bucketsHit >= 3 && isMulti) {
          return i + 1; // 1-indexed
        }
      }
      return null;
    }

    function buildResultUrl(baseUrl, bucket) {
      var url;
      try { url = new URL(baseUrl, window.location.origin); }
      catch (e) { return fallbackUrl || '/'; }

      if (bucket) url.searchParams.set('profile', bucket.toUpperCase());
      url.searchParams.set('score_generate', scores.generate);
      url.searchParams.set('score_shield', scores.shield);
      url.searchParams.set('score_route', scores.route);
      url.searchParams.set('score_compound', scores.compound);

      // Captured hero overlays (priority order, comma-separated)
      if (capturedOverlays.length > 0) {
        url.searchParams.set('overlay', capturedOverlays.join(','));
        // Also expose the primary (first) overlay for simple result-page consumers
        url.searchParams.set('overlay_primary', capturedOverlays[0]);
      }

      // Flatten answers into URL params
      Object.keys(answers).forEach(function (blockIdx) {
        var sels = answers[blockIdx];
        if (Array.isArray(sels) && sels.length > 0) {
          url.searchParams.set('q_' + blockIdx, sels.map(function (s) { return s.value; }).join(','));
        }
      });

      if (forwardUtm) {
        var incoming = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
          var v = incoming.get(k);
          if (v) url.searchParams.set(k, v);
        });
      }

      if (baseUrl && baseUrl.charAt(0) === '/' && baseUrl.indexOf('//') !== 0) {
        return url.pathname + url.search + url.hash;
      }
      return url.toString();
    }

    /* ───── Persistence ───── */

    function persist() {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({
          v: 2, currentIdx: currentIdx, history: history, answers: answers,
          sessionId: sessionId, startedAt: startedAt, savedAt: Date.now(),
        }));
      } catch (e) {}
    }

    function rehydrate() {
      try {
        var raw = sessionStorage.getItem(storageKey);
        if (!raw) return;
        var data = JSON.parse(raw);
        if (data.v !== 2 && data.v !== 1) return;
        if (typeof data.currentIdx !== 'number' || data.currentIdx > blocks.length) return;
        var targetBlock = blockAt(data.currentIdx);
        if (!targetBlock) return;
        if (targetBlock.dataset.blockType === 'loading') return;

        currentIdx = data.currentIdx;
        history = data.history || [currentIdx];
        answers = data.answers || {};

        // Backfill missing fields on v1 records
        Object.keys(answers).forEach(function (bi) {
          if (!Array.isArray(answers[bi])) return;
          answers[bi].forEach(function (sel) {
            if (typeof sel.extra === 'undefined') sel.extra = '';
            if (typeof sel.overlay === 'undefined') sel.overlay = 'none';
          });
        });

        // Restore visual state of inputs based on saved answers
        Object.keys(answers).forEach(function (bi) {
          var blockEl = blockAt(parseInt(bi, 10));
          if (!blockEl) return;
          answers[bi].forEach(function (sel) {
            var input = blockEl.querySelector('.qt-answer__input[value="' + sel.value + '"]');
            if (input) {
              input.checked = true;
              var label = input.closest('label');
              if (label) label.classList.add('qt-selected');
            }
          });
        });

        showBlockNoHistory(currentIdx);
        log('Rehydrated', { currentIdx: currentIdx, answersCount: Object.keys(answers).length });
      } catch (e) {
        log('Rehydrate failed', e);
      }
    }

    /* ───── Util ───── */

    function generateSessionId() {
      return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());